import React, { useState } from "react";
import { useForm } from "react-hook-form";
import { useModal } from "react-modal-hook";
import { TAG_DISPLAY } from "common/constants";
import { spacing } from "../../../../../../../../stitches.config";
import { FlexColumn } from "../../../../../../../Common/__styles__/Layout";
import {
  ButtonSection,
  Container,
  ContentSection,
  FormSection,
  HeaderSection,
  PrimaryButtons,
  SecondaryButtons,
} from "../../../../../../../Common/__styles__/Modal";
import { ButtonLink } from "../../../../../../../Common/ButtonLink";
import { Button } from "../../../../../../../Common/Buttons/Button";
import Modal from "../../../../../../../Common/Modal";
import { Select, Text } from "../../../../../../../Inputs/react-hook-form";
import type { DropdownOption } from "./types";

export function useCreateEditOptionModal() {
  const [showProps, setShowProps] = useState<
    Omit<CreateEditOptionModalParams, "onCancel">
  >({
    onSave: () => {},
    useAsTag: false,
    valueEditingDisabled: false,
  });
  const [_show, hide] = useModal(
    () => (
      <Modal onRequestClose={hide}>
        <CreateEditOptionModal onCancel={hide} {...showProps} />
      </Modal>
    ),
    [showProps],
  );

  function show({
    onDelete,
    onSave,
    ...params
  }: Omit<CreateEditOptionModalParams, "onCancel">) {
    setShowProps({
      onDelete:
        onDelete ?
          () => {
            onDelete();
            hide();
          }
        : undefined,
      onSave: props => {
        onSave(props);
        hide();
      },
      ...params,
    });

    _show();
  }

  return [show, hide] as const;
}

type CreateEditOptionModalParams = {
  existingOption?: DropdownOption;
  onCancel: () => void;
  onDelete?: () => void;
  onSave: (props: DropdownOption) => void;
  useAsTag?: boolean;
  valueEditingDisabled: boolean;
};

export function CreateEditOptionModal({
  existingOption,
  onCancel,
  onDelete,
  onSave,
  useAsTag,
  valueEditingDisabled,
}: CreateEditOptionModalParams) {
  let defaultValues: Partial<DropdownOption>;

  if (existingOption) {
    defaultValues = existingOption;
  } else if (useAsTag) {
    defaultValues = {
      display: TAG_DISPLAY.NEUTRAL,
    };
  } else {
    defaultValues = {};
  }

  const {
    control,
    formState: { errors, touchedFields, defaultValues: initialDefaultValues },
    handleSubmit,
    register,
    setValue,
    watch,
  } = useForm<DropdownOption>({
    defaultValues,
  });

  const headerVerb = existingOption ? "Edit" : "Add";
  const titleValue = watch("title");

  // it's a better user experience to respect that if the value and title are
  // different—either on first load or proxied by the value field being touched—
  // the user probably means them to be that way and to not keep the two
  // automatically synced
  const valueChangesWithTitle =
    !valueEditingDisabled &&
    !touchedFields.const &&
    initialDefaultValues?.const === initialDefaultValues?.title;

  return (
    <Container overflows>
      <HeaderSection>
        <h1>{headerVerb} dropdown option</h1>
      </HeaderSection>
      <FormSection overflows>
        <ContentSection overflows>
          <FlexColumn css={{ gap: spacing.m }}>
            <Text
              label="Label"
              required
              {...register("title", {
                required: "Label is required",
                onChange: e => {
                  if (valueChangesWithTitle) {
                    setValue("const", e.target.value);
                  }
                },
              })}
              error={errors.title?.message}
            />
            <Text
              label="Value"
              required
              disabled={!titleValue || valueEditingDisabled}
              error={errors.const?.message}
              {...register("const", { required: "Value is required" })}
            />
            {useAsTag && (
              <div>
                <Select
                  menuPlacement="auto"
                  name="display"
                  label="Tag color"
                  control={control}
                  error={errors.display?.message}
                  options={[
                    {
                      label: "Default - blue",
                      value: TAG_DISPLAY.DEFAULT,
                    },
                    {
                      label: "Neutral - grey",
                      value: TAG_DISPLAY.NEUTRAL,
                    },
                    {
                      label: "Warning - yellow",
                      value: TAG_DISPLAY.WARNING,
                    },
                    {
                      label: "Critical - red",
                      value: TAG_DISPLAY.CRITICAL,
                    },
                  ]}
                />
              </div>
            )}
          </FlexColumn>
        </ContentSection>
      </FormSection>
      <ButtonSection>
        {!!existingOption && onDelete && (
          <SecondaryButtons>
            <ButtonLink
              size="medium"
              styleVariant="critical"
              onClick={onDelete}
            >
              Delete
            </ButtonLink>
          </SecondaryButtons>
        )}
        <PrimaryButtons>
          <Button
            title="Cancel"
            variant="secondary"
            size="medium"
            onPress={onCancel}
          />

          <Button
            title="Save"
            variant="primary"
            size="medium"
            onClick={handleSubmit(onSave)}
          />
        </PrimaryButtons>
      </ButtonSection>
    </Container>
  );
}
